import { MapPin, Phone, Clock, Shield } from "lucide-react";
import { FaWhatsapp, FaInstagram, FaTelegram } from "react-icons/fa";
import { Link } from "react-router-dom";
import { useLanguage } from "../context/LanguageContext";

const Footer = () => {
  const { t, language } = useLanguage();
  const currentYear = new Date().getFullYear();
  const whatsappNumber = "+905488484828";

  const quickLinks = [
    { nameKey: "nav.services", href: "#services", testId: "services" },
    { nameKey: "nav.venue", href: "#venue", testId: "venue" },
    { nameKey: "nav.about", href: "#about", testId: "about" },
    { nameKey: "nav.gallery", href: "#gallery", testId: "gallery" },
    { nameKey: "nav.contact", href: "#contact", testId: "contact" }
  ];

  return (
    <footer className="bg-[#050505] border-t border-white/10 py-16" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-4 gap-12 mb-12">
          {/* Brand */}
          <div>
            <a href="#" className="inline-block mb-6">
              <img 
                src="https://customer-assets.emergentagent.com/job_web-yayini/artifacts/3lnxk1de_C4ED1F5A-9382-457C-A949-2A0F4A5E715E.png" 
                alt="First Lady VIP Services Logo" 
                className="h-16 w-auto"
              />
            </a>
            <p className="text-gray-500 text-sm leading-relaxed mb-6">
              {t("footer.description")}
            </p>
            <div className="flex gap-3">
              <a
                href={`https://wa.me/${whatsappNumber.replace(/\+/g, "")}`}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-white/5 border border-white/10 flex items-center justify-center text-gray-400 hover:text-[#25D366] hover:border-[#25D366]/50 transition-all"
                aria-label="WhatsApp"
                data-testid="footer-whatsapp"
              >
                <FaWhatsapp size={18} />
              </a>
              <a
                href="https://instagram.com/firstladynightclub"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-white/5 border border-white/10 flex items-center justify-center text-gray-400 hover:text-[#E1306C] hover:border-[#E1306C]/50 transition-all"
                aria-label="Instagram"
                data-testid="footer-instagram"
              >
                <FaInstagram size={18} />
              </a>
              <a
                href="#"
                className="w-10 h-10 bg-white/5 border border-white/10 flex items-center justify-center text-gray-400 hover:text-[#0088cc] hover:border-[#0088cc]/50 transition-all"
                aria-label="Telegram"
                data-testid="footer-telegram"
              >
                <FaTelegram size={18} />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-['Syne'] text-sm font-bold uppercase tracking-widest text-white mb-6">
              {t("footer.quickLinks")}
            </h4>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.testId}>
                  <a
                    href={link.href}
                    className="text-gray-500 hover:text-[#FF00FF] transition-colors text-sm"
                    data-testid={`footer-link-${link.testId}`}
                  >
                    {t(link.nameKey)}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="font-['Syne'] text-sm font-bold uppercase tracking-widest text-white mb-6">
              {t("footer.contact")}
            </h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <Phone size={16} className="text-[#FF00FF] mt-1 flex-shrink-0" />
                <a
                  href={`tel:${whatsappNumber}`}
                  className="text-gray-500 hover:text-white transition-colors text-sm"
                >
                  +90 548 848 4828
                </a>
              </li>
              <li className="flex items-start gap-3">
                <MapPin size={16} className="text-[#00FFFF] mt-1 flex-shrink-0" />
                <span className="text-gray-500 text-sm">{t("contact.locationValue")}</span>
              </li>
              <li className="flex items-start gap-3">
                <Clock size={16} className="text-[#D4AF37] mt-1 flex-shrink-0" />
                <span className="text-gray-500 text-sm">{t("contact.hoursValue")}</span>
              </li>
            </ul>
          </div>

          {/* Legal Links */}
          <div>
            <h4 className="font-['Syne'] text-sm font-bold uppercase tracking-widest text-white mb-6 flex items-center gap-2">
              <Shield size={16} className="text-[#D4AF37]" />
              {language === "tr" ? "Yasal" : "Legal"}
            </h4>
            <ul className="space-y-3">
              <li>
                <Link
                  to="/legal"
                  className="text-gray-500 hover:text-[#D4AF37] transition-colors text-sm flex items-center gap-2"
                  data-testid="footer-legal-link"
                >
                  {t("footer.legal")}
                </Link>
              </li>
              <li>
                <a
                  href="#"
                  className="text-gray-500 hover:text-[#D4AF37] transition-colors text-sm"
                  data-testid="footer-privacy-link"
                >
                  {t("footer.privacy")}
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="pt-8 border-t border-white/10 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-gray-600 text-xs">
            © {currentYear} First Lady VIP Services. {t("footer.copyright")}
          </p>
          <p className="text-gray-600 text-xs">
            <span className="text-[#00FFFF]">♦</span> {t("footer.vipExperience")} <span className="text-[#00FFFF]">♦</span>
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
